import * as React from "react";

type Theme = "dark" | "light" | "system";
type ResolvedTheme = "dark" | "light";

// Browser chrome colors for the theme-color metas in the application layout.
// The media-query meta only sees the OS scheme, never the in-app wish stored
// in localStorage, so applyTheme recolors both metas to the resolved theme.
// Held to tokens.json by the agreement test in
// spec/javascript/config/design-tokens-boundary.test.ts.
export const THEME_COLORS: Record<ResolvedTheme, string> = {
  light: "#C74A30",
  dark: "#151311",
};

interface ThemeProviderState {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
}

function resolveTheme(theme: Theme): ResolvedTheme {
  if (theme === "system") {
    if (typeof window === "undefined") return "light";
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return theme;
}

const ThemeProviderContext = React.createContext<ThemeProviderState | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
}

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "amealio-theme",
}: ThemeProviderProps) {
  const [theme, setThemeState] = React.useState<Theme>(defaultTheme);
  // Lazy statt fest "light": ohne das baut jede Karte (`PlaceMap`, an
  // `resolvedTheme` gebunden) für einen Dark-Mode-Besucher beim ersten Mount
  // mit `colorScheme: "light"`, nur um Sekundenbruchteile später komplett neu
  // aufgebaut zu werden, sobald der Effekt unten den echten Wert nachträgt.
  // Der Lazy-Initializer läuft synchron im ersten Render und kennt den echten
  // Wert damit schon dort, ohne das SSR-Verhalten zu ändern (kein `window` ->
  // weiterhin "light").
  const [resolvedTheme, setResolvedTheme] = React.useState<ResolvedTheme>(() => {
    if (typeof window === "undefined") return "light";

    try {
      const stored = window.localStorage.getItem(storageKey) as Theme | null;
      return resolveTheme(stored ?? defaultTheme);
    } catch {
      return resolveTheme(defaultTheme);
    }
  });

  const applyTheme = React.useCallback((nextTheme: Theme) => {
    const resolved = resolveTheme(nextTheme);
    setResolvedTheme(resolved);

    if (typeof window === "undefined") return;
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(resolved);
    const metas = window.document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]');
    for (const meta of metas) {
      meta.setAttribute("content", THEME_COLORS[resolved]);
    }
  }, []);

  React.useEffect(() => {
    let stored: Theme | null = null;
    try {
      stored = window.localStorage.getItem(storageKey) as Theme | null;
    } catch {
      // localStorage access throws under blocked cookies / private mode. Fall
      // back to the default instead of letting the effect crash and unmount the
      // whole app (this provider wraps the root and has no error boundary).
      stored = null;
    }
    const initial = stored ?? defaultTheme;
    setThemeState(initial);
    applyTheme(initial);

    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const listener = () => {
      if (theme === "system") {
        applyTheme("system");
      }
    };

    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, [applyTheme, defaultTheme, storageKey, theme]);

  const setTheme = React.useCallback(
    (nextTheme: Theme) => {
      if (typeof window !== "undefined") {
        try {
          window.localStorage.setItem(storageKey, nextTheme);
        } catch {
          // Persistence is best-effort; ignore blocked-storage failures so the
          // toggle still applies for the current session.
        }
      }
      setThemeState(nextTheme);
      applyTheme(nextTheme);
    },
    [applyTheme, storageKey],
  );

  const value = React.useMemo(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme],
  );

  return <ThemeProviderContext.Provider value={value}>{children}</ThemeProviderContext.Provider>;
}

export function useTheme(): ThemeProviderState {
  const context = React.useContext(ThemeProviderContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
